// Plan configuration and limits
export type PlanType = 'free' | 'bedroom' | 'pro' | 'studio';

export interface PlanLimits {
  storage: number; // bytes
  maxFileSize: number; // bytes
  versioning: number | 'unlimited';
  trafficLimit: number | 'unlimited'; // bytes - traffic/bandwidth limit
  features: string[];
}

export interface PlanHighlight {
  title: string;
  description?: string;
}

export interface PlanConfig {
  id: PlanType;
  name: string;
  displayName: string;
  tagline: string;
  description: string;
  price: {
    monthly: number;
    yearly: number;
  };
  limits: PlanLimits;
  highlights: PlanHighlight[];
  accentColor: string;
  badge?: string;
  popular?: boolean;
  stripePriceIdEnv?: {
    monthly: string;
    yearly: string;
  };
}

// Storage constants
const GB = 1024 * 1024 * 1024;
const MB = 1024 * 1024;
const TB = 1024 * GB;

// Plan configurations
export const PLANS: Record<PlanType, PlanConfig> = {
  free: {
    id: 'free',
    name: 'Free',
    displayName: 'Free',
    tagline: 'Start for free and explore fyle without risk',
    description: 'Perfect for organising early projects and testing workflows.',
    price: {
      monthly: 0,
      yearly: 0,
    },
    limits: {
      storage: 1 * GB, // 1 GB
      maxFileSize: 50 * MB, // 50 MB
      versioning: 4,
      trafficLimit: 2 * TB, // 2TB traffic
      features: [
        'ai_tagging',
        'bpm_detection',
        'key_detection',
        'collaboration',
        'batch_operations',
      ],
    },
    highlights: [
      {
        title: '1 GB cloud storage',
        description: 'Ideal for single projects and quick testing.',
      },
      {
        title: '4 Version creations',
        description: 'Per month. Keep track of your iterations.',
      },
      {
        title: '1 active beatpack',
        description: 'Share your beats with one active link.',
      },
      {
        title: '4 YouTube Uploads',
        description: 'Upload your music to YouTube monthly.',
      },
    ],
    accentColor: 'from-slate-500 to-slate-700',
  },

  bedroom: {
    id: 'bedroom',
    name: 'Bedroom',
    displayName: 'BEDROOM',
    tagline: '250 GB of power built for ambitious bedroom producers',
    description: 'Organise your sessions with managed Hetzner storage and version control.',
    price: {
      monthly: 5.9,
      yearly: 5.32 * 12, // Yearly price with -10% discount (5.32 monthly equivalent)
    },
    limits: {
      storage: 250 * GB, // 250 GB
      maxFileSize: 5 * GB, // 5 GB Upload-Limit
      versioning: 31,
      trafficLimit: 'unlimited', // Unlimited traffic
      features: [
        'ai_tagging',
        'bpm_detection',
        'key_detection',
        'collaboration',
        'batch_operations',
        'advanced_search',
        'custom_tags',
      ],
    },
    highlights: [
      {
        title: '250 GB cloud storage',
        description: 'Ready for scalable Object Storage Workflows - perfect for growing libraries.',
      },
      {
        title: '31 Version creations',
        description: 'Per month. Keep every iteration of your beats in view.',
      },
      {
        title: 'Unlimited beatpacks',
        description: 'Share as many beatpacks as you need.',
      },
      {
        title: '31 YouTube videos',
        description: 'Upload your music to YouTube monthly.',
      },
    ],
    accentColor: 'from-emerald-400 to-teal-500',
    badge: 'New',
    stripePriceIdEnv: {
      monthly: 'VITE_STRIPE_PRICE_BEDROOM_MONTHLY',
      yearly: 'VITE_STRIPE_PRICE_BEDROOM_YEARLY',
    },
  },

  pro: {
    id: 'pro',
    name: 'Pro',
    displayName: 'PRO',
    tagline: '500 GB storage and unlimited versioning for power users',
    description: 'For producers with high output and teams that need to stay in sync.',
    price: {
      monthly: 12.9,
      yearly: 11.6 * 12, // Yearly price with -10% discount (9.82 monthly equivalent)
    },
    limits: {
      storage: 500 * GB, // 500 GB
      maxFileSize: 10 * GB, // 10 GB Upload-Limit
      versioning: 'unlimited',
      trafficLimit: 'unlimited', // Unlimited traffic
      features: [
        'ai_tagging',
        'bpm_detection',
        'key_detection',
        'collaboration',
        'batch_operations',
        'advanced_search',
        'vst_integration', // 🔥 VST from PRO
        'api_access',
        'custom_tags',
        'version_history',
      ],
    },
    highlights: [
      {
        title: '500 GB cloud storage',
        description: 'Ready to use and optimised for collaboration.',
      },
      {
        title: 'Unlimited Version creations',
        description: 'Every take, mixdown, and master stays secured.',
      },
      {
        title: '62 YouTube Videos',
        description: 'Upload your music to YouTube monthly.',
      },
      {
        title: 'Unlimited Beatpacks',
        description: 'Share as many beatpacks as you need.',
      },
    ],
    accentColor: 'from-indigo-500 to-blue-500',
    badge: 'Most Popular',
    popular: true,
    stripePriceIdEnv: {
      monthly: 'VITE_STRIPE_PRICE_PRO_MONTHLY',
      yearly: 'VITE_STRIPE_PRICE_PRO_YEARLY',
    },
  },

  studio: {
    id: 'studio',
    name: 'Studio',
    displayName: 'STUDIO',
    tagline: '5 TB and enterprise tools for studios and labels',
    description: 'Everything from PRO plus dedicated services for large teams.',
    price: {
      monthly: 49.9,
      yearly: 44.9 * 12, // Yearly price with -10% discount (40.40 monthly equivalent)
    },
    limits: {
      storage: 5 * TB, // 5 TB
      maxFileSize: 20 * GB, // 20 GB Upload-Limit
      versioning: 'unlimited',
      trafficLimit: 'unlimited', // Unlimited traffic
      features: [
        'ai_tagging',
        'bpm_detection',
        'key_detection',
        'collaboration',
        'batch_operations',
        'advanced_search',
        'vst_integration',
        'vst_priority', // Priority VST
        'api_access',
        'api_webhooks',
        'custom_tags',
        'custom_ai_models',
        'stem_separation',
        'version_history',
        'priority_support',
      ],
    },
    highlights: [
      {
        title: '5 TB Storage',
        description: 'Redundancy for large session databases and stems.',
      },
      {
        title: 'Unlimited Version creations',
        description: 'Every take, mixdown, and master stays secured.',
      },
      {
        title: 'Unlimited YouTube videos',
        description: 'Upload as many videos as you need.',
      },
      {
        title: 'Unlimited beatpacks',
        description: 'Share as many beatpacks as you need.',
      },
      {
        title: 'Support priority',
        description: 'Direct access to the fyle team and tailored onboarding.',
      },
    ],
    accentColor: 'from-purple-500 to-fuchsia-500',
    badge: 'Ultimate',
    stripePriceIdEnv: {
      monthly: 'VITE_STRIPE_PRICE_STUDIO_MONTHLY',
      yearly: 'VITE_STRIPE_PRICE_STUDIO_YEARLY',
    },
  },
};

// Storage Add-ons
export interface StorageAddon {
  id: string;
  name: string;
  storage: number; // bytes
  eligibleFromPlan: Exclude<PlanType, 'free'>;
  price: {
    monthly: number;
    yearly: number;
  };
}

export const STORAGE_ADDONS: StorageAddon[] = [
  {
    id: 'addon_250gb',
    name: '250 GB',
    storage: 250 * GB,
    eligibleFromPlan: 'bedroom',
    price: { monthly: 3.9, yearly: 3.9 * 12 },
  },
  {
    id: 'addon_500gb',
    name: '500 GB',
    storage: 500 * GB,
    eligibleFromPlan: 'bedroom',
    price: { monthly: 6.9, yearly: 6.9 * 12 },
  },
];

// Helper functions
export const getPlanConfig = (plan: PlanType): PlanConfig => {
  return PLANS[plan];
};

export const hasFeature = (plan: PlanType, feature: string): boolean => {
  return PLANS[plan].limits.features.includes(feature);
};

export const canUploadFile = (
  plan: PlanType,
  currentStorage: number,
  fileSize: number
): { allowed: boolean; reason?: string } => {
  const config = PLANS[plan];
  
  // Check file size limit
  if (fileSize > config.limits.maxFileSize) {
    return {
      allowed: false,
      reason: `File too large. Max size for ${plan.toUpperCase()} plan is ${formatBytes(config.limits.maxFileSize)}`,
    };
  }
  
  // Check storage limit
  if (currentStorage + fileSize > config.limits.storage) {
    return {
      allowed: false,
      reason: `Not enough storage. Upgrade your plan or add storage.`,
    };
  }
  
  return { allowed: true };
};

export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format(price);
};

export const getPlanStripePriceId = (
  plan: PlanType,
  interval: 'monthly' | 'yearly'
): string | undefined => {
  const config = PLANS[plan];
  if (!config.stripePriceIdEnv) {
    return undefined;
  }
  const envKey = config.stripePriceIdEnv[interval];
  return (import.meta.env as Record<string, string | undefined>)[envKey];
};

/**
 * Updates plan prices with Stripe price data
 * @param planId - The plan ID to update
 * @param stripePrices - Map of Stripe prices keyed by "planId_interval"
 * @returns Updated plan config with Stripe prices, or original if no Stripe prices found
 */
export const updatePlanWithStripePrices = (
  planId: PlanType,
  stripePrices: Map<string, { amount: number; currency: string }>
): PlanConfig => {
  const plan = PLANS[planId];
  const monthlyKey = `${planId}_monthly`;
  const yearlyKey = `${planId}_yearly`;
  const monthlyPrice = stripePrices.get(monthlyKey);
  const yearlyPrice = stripePrices.get(yearlyKey);

  if (!monthlyPrice && !yearlyPrice) {
    return plan;
  }

  return {
    ...plan,
    price: {
      monthly: monthlyPrice ? monthlyPrice.amount / 100 : plan.price.monthly,
      yearly: yearlyPrice ? yearlyPrice.amount / 100 : plan.price.yearly,
    },
  };
};

